import {
    ColorClubMatas4,
    ColorNeutralBlack,
    FontSize16,
    FontSize18,
    FontSize24,
    FontSize32,
    Spacing16,
    Spacing20,
    Spacing40
} from 'autogen/design-tokens/tokens';
import { type CountDownBannerModel, HideCountdownBanner } from 'autogen/swagger/all';
import { CloseButtonAriaLabel } from 'autogen/translation-keys/trans-website-booking-onetoonebanner-beforebooking';
import { WatchItHere, WatchVideoHere } from 'autogen/translation-keys/trans-website-liveevent-banner';
import { ButtonWithIcon } from 'components/Button';
import Icon from 'components/Icon';
import SimpleCountdown from 'components/liveEvents/simpleCountdown';
import PageAction from 'components/shared/Routing/PageAction';
import format from 'helpers/translations';
import { SvgSprite } from 'modules/svgSprite';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { breakpoint, sizes } from 'variables';

const Container = styled.div`
    display: block;
    height: 150px;
    background-color: ${ColorClubMatas4};
    margin-bottom: ${Spacing16};

    ${breakpoint.up(sizes.xl)} {
        height: 200px;
    }
`;

const Fluid = styled.div`
    position: relative;
    height: 100%;
`;

const StyledPageAction = styled(PageAction)`
    &:focus,
    &:active,
    &:hover {
        text-decoration: none;
    }
`;

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: 100%;

    ${breakpoint.up(sizes.md)} {
        flex-direction: row;
    }
`;

const Text = styled.div`
    ${breakpoint.up(sizes.md)} {
        margin-left: ${Spacing20};
    }

    ${breakpoint.up(sizes.xl)} {
        flex: 1;
        margin-right: ${Spacing40};
    }
`;

const StyledButtonWithIcon = styled(ButtonWithIcon)`
    position: absolute;
    right: 0;
    border: none;
    background: transparent;
    margin: ${Spacing16};
`;

const BannerTitle = styled.p`
    color: ${ColorNeutralBlack};
    font-weight: bold;
    letter-spacing: 0;
    font-size: ${FontSize18};
    line-height: 20px;
    text-align: center;
    margin-bottom: 10px;
    quotes: '“' '”';

    &:before {
        content: open-quote;
    }

    &:after {
        content: close-quote;
    }

    ${breakpoint.up(sizes.xl)} {
        font-size: ${FontSize32};
        line-height: 40px;
    }
`;

const BannerText = styled.p`
    color: ${ColorNeutralBlack};
    letter-spacing: 0;
    font-size: ${FontSize16};
    line-height: 24px;
    text-align: center;
    margin-bottom: 10px;

    ${breakpoint.up(sizes.xl)} {
        font-size: ${FontSize24};
        line-height: 32px;
    }
`;

const PlayLogo = styled(SvgSprite)`
    width: 50px;
    height: 50px;

    ${breakpoint.up(sizes.md)} {
        width: 100px;
        height: 100px;
    }

    ${breakpoint.up(sizes.xl)} {
        width: 150px;
        height: 150px;
    }
`;

const AnimatedPlayLogo = styled.div`
    flex: none;
    width: 65px;
    height: 65px;
    background-image: url(/images/matas-live-play-animated.svg);

    ${breakpoint.up(sizes.md)} {
        width: 100px;
        height: 100px;
    }

    ${breakpoint.up(sizes.xl)} {
        width: 150px;
        height: 150px;
    }
`;

const LiveEventBanner = ({ model }: { model: CountDownBannerModel }): JSX.Element | null => {
    const {
        name,
        startTime,
        endTime,
        pageAction,
        overriddenCountdownBannerText,
        overriddenLiveBannerText,
        overriddenEndedBannerText
    } = model;
    const [hideBanner, setHideBanner] = useState<boolean>(false);
    const { t } = useTranslation();

    const closeBanner = (): void => {
        setHideBanner(true);
        HideCountdownBanner();
    };

    const startTimeDate = new Date(startTime);
    const isBeforeEvent = startTimeDate.getTime() > Date.now();
    const isLive = startTimeDate.getTime() < Date.now() && new Date(endTime).getTime() > Date.now();
    const isAfterEvent = new Date(endTime).getTime() < Date.now();

    const getLabel = () => {
        if (isBeforeEvent) return 'eventCountdown';
        if (isLive) return 'eventLive';
        return 'eventEnded';
    };
    const trackingLabel = getLabel;
    const trackingClass = `tracking_liveEvent_bannerClick_${trackingLabel}`;

    return !hideBanner ? (
        <Container>
            <Fluid className="container-fluid">
                <StyledButtonWithIcon
                    aria-label={t(CloseButtonAriaLabel)}
                    onClick={closeBanner}
                    variant="ghostOnLight"
                    icon={<Icon type="Close" size={16} />}
                    size="sm"
                />
                <StyledPageAction action={pageAction} className={trackingClass}>
                    {isBeforeEvent && (
                        <Wrapper>
                            <SimpleCountdown startTime={startTimeDate} />
                            <Text>
                                <BannerTitle>{name}</BannerTitle>
                                <BannerText>{format(t(WatchItHere), overriddenCountdownBannerText)}</BannerText>
                            </Text>
                        </Wrapper>
                    )}
                    {isLive && (
                        <Wrapper>
                            <AnimatedPlayLogo />
                            <Text>
                                <BannerTitle>{name}</BannerTitle>
                                <BannerText>{format(t(WatchItHere), overriddenLiveBannerText)}</BannerText>
                            </Text>
                        </Wrapper>
                    )}
                    {isAfterEvent && (
                        <Wrapper>
                            <PlayLogo sprite="LivePlay" />
                            <Text>
                                <BannerTitle>{name}</BannerTitle>
                                <BannerText>{format(t(WatchVideoHere), overriddenEndedBannerText)}</BannerText>
                            </Text>
                        </Wrapper>
                    )}
                </StyledPageAction>
            </Fluid>
        </Container>
    ) : null;
};

export default LiveEventBanner;
